import supabase from '../config/supabase';
import config from '../config/environment';
import { PORTAL_USER_INACTIVE_ERROR } from '../utils/portalSession';

export type PortalSessionInvalidatedReason = 'inactive' | 'unauthenticated';
type PortalSessionInvalidatedListener = (reason: PortalSessionInvalidatedReason) => void;

let portalSessionInvalidatedListener: PortalSessionInvalidatedListener | null = null;

export function onPortalSessionInvalidated(
  listener: PortalSessionInvalidatedListener | null,
): void {
  portalSessionInvalidatedListener = listener;
}

function notifyPortalSessionInvalidated(reason: PortalSessionInvalidatedReason): void {
  portalSessionInvalidatedListener?.(reason);
}

function readPayloadError(payload: {
  error?: string | { message?: string; code?: string };
  message?: string;
}): string {
  if (typeof payload.error === 'string') {
    return payload.error;
  }

  if (payload.error && typeof payload.error === 'object') {
    if (payload.error.code === PORTAL_USER_INACTIVE_ERROR) {
      return PORTAL_USER_INACTIVE_ERROR;
    }

    if (typeof payload.error.message === 'string') {
      return payload.error.message;
    }
  }

  if (typeof payload.message === 'string') {
    return payload.message;
  }

  return 'Erro na comunicação com o servidor.';
}

function isUnauthenticatedError(status: number, message: string): boolean {
  if (status !== 401) {
    return false;
  }

  const normalized = message.trim().toLowerCase();
  return normalized === 'unauthorized'
    || normalized === 'usuária não autenticada.'
    || normalized === 'usuária não autenticada. faça login novamente.'
    || normalized.includes('não autenticada');
}

type QueryPrimitive = string | number | boolean;
type QueryValue = QueryPrimitive | QueryPrimitive[] | null | undefined;

export interface EdgeFunctionRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  requireAuth?: boolean;
  signal?: AbortSignal;
}

const appendQueryValues = (url: URL, key: string, value: QueryValue) => {
  if (value == null) {
    return;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return;
    }

    url.searchParams.set(key, value.map((item) => String(item)).join(','));
    return;
  }

  url.searchParams.set(key, String(value));
};

async function getAccessToken(requireAuth: boolean): Promise<string | null> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token?.trim() ?? '';

  if (!accessToken) {
    if (requireAuth) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    return null;
  }

  return accessToken;
}

export async function requestEdgeFunction<T>(
  functionName: string,
  options: EdgeFunctionRequestOptions = {},
): Promise<T> {
  if (!config.supabaseFunctionsUrl) {
    throw new Error('URL das Edge Functions não configurada.');
  }

  const requireAuth = options.requireAuth !== false;
  const accessToken = await getAccessToken(requireAuth);
  const url = new URL(`${config.supabaseFunctionsUrl}/${functionName}`);

  Object.entries(options.query ?? {}).forEach(([key, value]) => {
    appendQueryValues(url, key, value);
  });

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: config.supabaseAnonKey,
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers,
    signal: options.signal,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });

  const payload = await response.json().catch(() => ({})) as T & {
    error?: string | { message?: string; code?: string };
    message?: string;
  };

  if (!response.ok) {
    const errorMessage = readPayloadError(payload);

    if (requireAuth && errorMessage === PORTAL_USER_INACTIVE_ERROR) {
      notifyPortalSessionInvalidated('inactive');
    } else if (requireAuth && isUnauthenticatedError(response.status, errorMessage)) {
      notifyPortalSessionInvalidated('unauthenticated');
    }

    throw new Error(errorMessage);
  }

  return payload;
}
